import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { usePermission } from '../../../core/auth/usePermission';
import { hrApi } from '../api';
import { LeaveBalances } from '../components/LeaveBalances';
import { EmployeeDocumentsPanel } from '../components/EmployeeDocumentsPanel';
import { EmployeeAgreementsPanel } from '../components/EmployeeAgreementsPanel';
import type { EmployeeWithUser, UpdateEmployeeInput } from '../types';

export function EmployeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const canWriteEmployees = usePermission('hr.employees.write');
  const { data, loading, reload } = useApi<{ employee: EmployeeWithUser }>(id ? `/api/hr/employees/${id}` : null);
  const [form, setForm] = React.useState<UpdateEmployeeInput>({});
  const [saving, setSaving] = React.useState(false);
  const [teammates, setTeammates] = React.useState<EmployeeWithUser[]>([]);

  React.useEffect(() => {
    if (data?.employee) {
      const e = data.employee;
      setForm({
        employee_no: e.employee_no, department: e.department, designation: e.designation,
        hire_date: e.hire_date, scheduled_clock_in: e.scheduled_clock_in, scheduled_clock_out: e.scheduled_clock_out,
        hourly_rate: e.hourly_rate ? Number(e.hourly_rate) : null, is_active: e.is_active,
        monthly_base_salary: e.monthly_base_salary ? Number(e.monthly_base_salary) : null,
        termination_date: e.termination_date ?? null,
        agent_required: e.agent_required, manager_id: e.manager_id,
      });
    }
  }, [data]);

  React.useEffect(() => {
    hrApi.listEmployees().then(setTeammates).catch(() => setTeammates([]));
  }, []);

  if (loading) return <LoadingState variant="page" />;
  const employee = data?.employee;
  if (!employee) return <div className="p-6 text-sm text-muted-foreground">Employee not found.</div>;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    try {
      // Blank bank fields mean "keep the stored value" — strip them so an
      // untouched (or cleared) input never wipes what's on file.
      const patch = { ...form };
      if (!patch.bank_account_number) delete patch.bank_account_number;
      if (!patch.bank_routing_number) delete patch.bank_routing_number;
      if (patch.bank_account_type === undefined) delete patch.bank_account_type;
      await hrApi.updateEmployee(id, patch);
      toast.success('Saved');
      reload();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally { setSaving(false); }
  }

  // Payroll's buildBaseComponent prefers monthly_base_salary and only falls
  // back to hourly_rate when it is null — so "both set" is not an error, it
  // silently means the hourly rate is dead. Say so instead of blocking the save.
  const bothPaySet = !!form.monthly_base_salary && !!form.hourly_rate;

  const initial = (employee.name?.[0] ?? '?').toUpperCase();
  const status = !employee.has_password
    ? <span className={statusPill('warning')}><span className={statusDot('warning')} />Invite pending</span>
    : !employee.is_active
      ? <span className={statusPill('destructive')}><span className={statusDot('destructive')} />Inactive</span>
      : <span className={statusPill('success')}><span className={statusDot('success')} />Active</span>;

  return (
    <div>
      <PageHeader
        eyebrow={<><Link to="/dashboard/hr/team" className="hover:text-foreground transition-colors">People</Link> / {employee.name}</>}
        title={employee.name}
        chip={status}
        description={employee.email}
        actions={
          <Link to="/dashboard/hr/team">
            <Button variant="ghost" type="button" className="rounded-full h-10 px-3 text-[13px] font-semibold gap-1.5 text-muted-foreground">
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to team
            </Button>
          </Link>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-4xl space-y-6">
        {/* Hero card with avatar */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: 'spring', stiffness: 110, damping: 22 }}
          className={[VISUAL.bento, 'p-7 flex items-center gap-5'].join(' ')}
        >
          <div className="h-16 w-16 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center text-[20px] font-bold text-saffron-foreground shrink-0">
            {initial}
          </div>
          <div className="min-w-0 flex-1">
            <p className={[VISUAL.eyebrow, 'mb-1'].join(' ')}>Employee</p>
            <p className="font-sans font-semibold text-[22px] tracking-[-0.02em] text-foreground truncate">{employee.name}</p>
            <p className="text-[12.5px] text-muted-foreground mt-0.5 truncate">
              {employee.department ?? 'No department'}
              {employee.designation ? ` · ${employee.designation}` : ''}
              {employee.employee_no ? ` · #${employee.employee_no}` : ''}
            </p>
          </div>
        </motion.section>

        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Leave balances · {new Date().getUTCFullYear()}</h3>
          <LeaveBalances employeeId={employee.id} />
        </section>

        <form onSubmit={save} className="space-y-6">
          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Role & employment</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Employee #"  value={form.employee_no ?? ''} onChange={(v) => setForm({ ...form, employee_no: v || null })} />
              <Field label="Department"  value={form.department  ?? ''} onChange={(v) => setForm({ ...form, department: v || null })} />
              <Field label="Designation" value={form.designation ?? ''} onChange={(v) => setForm({ ...form, designation: v || null })} />
              <Field label="Hire date" type="date" value={form.hire_date ?? ''} onChange={(v) => setForm({ ...form, hire_date: v || null })} />
              <div className="grid gap-1.5">
                <Label>Manager</Label>
                <Select
                  value={form.manager_id ?? '__none__'}
                  onValueChange={(v) => setForm({ ...form, manager_id: v === '__none__' ? null : v })}
                >
                  <SelectTrigger><SelectValue placeholder="No manager" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No manager</SelectItem>
                    {teammates.filter((t) => t.id !== employee.id).map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name ?? t.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Schedule & pay</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Scheduled clock-in" type="time" value={form.scheduled_clock_in ?? ''} onChange={(v) => setForm({ ...form, scheduled_clock_in: v || null })} />
              <Field label="Scheduled clock-out" type="time" value={form.scheduled_clock_out ?? ''} onChange={(v) => setForm({ ...form, scheduled_clock_out: v || null })} />
            </div>

            <div className="mt-6 pt-5 border-t border-border">
              <p className="text-[13.5px] font-semibold text-foreground">How this person is paid</p>
              <p className="text-[12.5px] text-muted-foreground mt-0.5 mb-4">
                Set <strong>one</strong> of the two. A monthly salary is paid as a flat amount each
                payroll run (prorated if they joined or left mid-period); an hourly rate is paid on
                recorded attendance hours. {bothPaySet && (
                  <span className="text-amber-700 dark:text-amber-400 font-semibold">
                    Both are set — payroll will use the monthly salary and ignore the hourly rate.
                    Clear one to remove the ambiguity.
                  </span>
                )}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Monthly salary"
                  type="number"
                  value={form.monthly_base_salary?.toString() ?? ''}
                  onChange={(v) => setForm({ ...form, monthly_base_salary: v ? Number(v) : null })}
                  hint="Salaried. Leave blank for an hourly employee."
                />
                <Field
                  label="Hourly rate"
                  type="number"
                  value={form.hourly_rate?.toString() ?? ''}
                  onChange={(v) => setForm({ ...form, hourly_rate: v ? Number(v) : null })}
                  hint="Paid per attendance hour. Ignored when a monthly salary is set."
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <Field
                  label="Last working day"
                  type="date"
                  value={form.termination_date ?? ''}
                  onChange={(v) => setForm({ ...form, termination_date: v || null })}
                  hint="Stops payroll after this date (salary is prorated to it). This does NOT deactivate the employee or suspend their sign-in — use Account state below for that."
                />
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer mt-6">
              <input
                type="checkbox"
                checked={form.agent_required === true}
                onChange={(e) => setForm({ ...form, agent_required: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-border accent-saffron"
              />
              <span className="flex-1">
                <span className="text-[13.5px] font-semibold text-foreground block">Monitored employee (desktop app required)</span>
                <span className="text-[12.5px] text-muted-foreground block mt-0.5">
                  When on, the install &amp; setup card appears automatically on this person's own
                  dashboard, and clocking in from the browser is disabled — they clock in through the
                  Worqly desktop app. Off = regular employee (web clock in/out, no app, no monitoring).
                  Monitored employees bill at the monitoring seat rate.
                </span>
              </span>
            </label>
          </section>

          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={VISUAL.dashSection}>Bank details (ACH)</h3>
              {employee.has_bank_details ? (
                <span className={statusPill('success')}><span className={statusDot('success')} />On file</span>
              ) : (
                <span className={statusPill('muted')}><span className={statusDot('muted')} />Not set</span>
              )}
            </div>
            <p className="text-[12.5px] text-muted-foreground mb-5">
              Used only for payroll direct-deposit (NACHA) exports. Stored values are masked
              {employee.has_bank_details ? ` — currently ${employee.bank_account_number} / ${employee.bank_routing_number}.` : '.'}
              {' '}Leave fields blank to keep the current values.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field
                label="Account number"
                value={typeof form.bank_account_number === 'string' ? form.bank_account_number : ''}
                onChange={(v) => setForm({ ...form, bank_account_number: v })}
              />
              <Field
                label="Routing number"
                value={typeof form.bank_routing_number === 'string' ? form.bank_routing_number : ''}
                onChange={(v) => setForm({ ...form, bank_routing_number: v })}
              />
              <div className="grid gap-1.5">
                <Label>Account type</Label>
                <select
                  value={form.bank_account_type ?? employee.bank_account_type ?? ''}
                  onChange={(e) => setForm({ ...form, bank_account_type: (e.target.value || null) as 'checking' | 'savings' | null })}
                  className="h-10 rounded-md border border-input bg-background px-3 text-[13.5px] text-foreground"
                >
                  <option value="">—</option>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
              </div>
            </div>
          </section>

          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <h3 className={[VISUAL.dashSection, 'mb-2'].join(' ')}>Account state</h3>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                id="is_active"
                checked={form.is_active ?? true}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-border accent-saffron"
              />
              <span className="flex-1">
                <span className="text-[13.5px] font-semibold text-foreground block">Active employee</span>
                <span className="text-[12.5px] text-muted-foreground block mt-0.5">
                  Unchecking removes them from new payroll runs <strong>and suspends their sign-in</strong> —
                  they can't log in until re-activated. Existing payslips and records stay. For an
                  offboarding where the final period still has to be paid, set a <strong>last working
                  day</strong> above first and deactivate after that run.
                </span>
              </span>
            </label>
          </section>

          <div className="flex items-center justify-between pt-2">
            <Link to="/dashboard/hr/team" className="text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors">
              Back without saving
            </Link>
            <Button type="submit" disabled={saving} className="rounded-full h-11 px-6 text-[14px] font-semibold">
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>

        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Documents</h3>
          <EmployeeDocumentsPanel employeeId={employee.id} canWrite={canWriteEmployees} />

          {/* E-sign agreements this employee signed or that are filed against
              their record. Same permission gate as the record itself. */}
          <div className="mt-7 pt-6 border-t border-border/40">
            <h4 className={[VISUAL.dashSection, 'mb-4'].join(' ')}>Signed agreements</h4>
            <EmployeeAgreementsPanel employeeId={employee.id} />
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', hint }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; hint?: string;
}) {
  // Explicit htmlFor/id: pay and offboarding fields carry hints that only help
  // if the label is actually associated with its input (screen readers + tests).
  const id = React.useId();
  const hintId = `${id}-hint`;
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-describedby={hint ? hintId : undefined}
      />
      {hint && <p id={hintId} className="text-[11.5px] text-muted-foreground leading-snug">{hint}</p>}
    </div>
  );
}
